<script setup lang="ts">
/**
 * One row of a list inside a game window: an inset steel well with a picture
 * on the left (a painted icon or any image in the `art` slot), the title in
 * game lettering, an optional subtitle and a progress bar, and the action on
 * the right (`aside` slot). The "every row names a thing with a picture" rule
 * of the game kit (life-layer.md, 2026-10-09).
 */
import GameIcon from './GameIcon.vue';
import type { GameIconName } from './icons';

withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    icon?: GameIconName;
    /** 0..1 — draws the kit progress bar under the title. */
    progress?: number | null;
    /** A finished or owned row: a gold rim. */
    done?: boolean;
  }>(),
  { subtitle: '', icon: undefined, progress: null, done: false },
);
</script>

<template>
  <div class="k-well row" :class="{ done }">
    <div class="art">
      <slot name="art"><GameIcon v-if="icon" :name="icon" :size="56" /></slot>
    </div>
    <div class="main">
      <span class="k-label title">{{ title }}</span>
      <span v-if="subtitle" class="sub">{{ subtitle }}</span>
      <span v-if="progress !== null" class="bar" aria-hidden="true">
        <span class="fill" :style="{ '--v': Math.max(0, Math.min(1, progress)) }"></span>
      </span>
      <slot />
    </div>
    <div v-if="$slots.aside" class="aside"><slot name="aside" /></div>
  </div>
</template>

<style scoped>
.row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 10px 14px 10px 10px;
}

.row.done {
  box-shadow:
    inset 0 2px 6px rgba(0, 0, 0, 0.55),
    0 0 0 2px #c99a3c;
}

.art {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  background: radial-gradient(circle at 50% 40%, rgba(120, 150, 180, 0.25), rgba(0, 0, 0, 0) 70%);
}

.art :deep(img) {
  max-width: 100%;
  max-height: 100%;
}

.main {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.title {
  font-size: 1.05rem;
}

.sub {
  font-family: var(--font-game-body);
  font-weight: 500;
  font-size: 0.85rem;
  color: #b8c7d6;
}

/* The kit progress bar: a dark groove with a glossy amber fill. */
.bar {
  position: relative;
  height: 12px;
  border-radius: 999px;
  background: #070c12;
  box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.8), 0 1px 0 rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.fill {
  position: absolute;
  inset: 2px auto 2px 2px;
  width: calc((100% - 4px) * var(--v));
  border-radius: 999px;
  background: linear-gradient(180deg, #ffe08a, #f0a92a 55%, #c47a12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: width 500ms var(--ease);
}

.aside {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 560px) {
  .row {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .aside {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }
}
</style>
